import { EyeSlash, Prohibit, ShieldCheck, X } from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import { moderation } from '../../api/endpoints';
import type { ActionModeration, Signalement } from '../../firebase/moderation';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState, Tabs } from '../../components/ui/Bits';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { useAsync } from '../../hooks/useAsync';
import { timeAgo } from '../../utils/format';
import { t } from '../../i18n';

/**
 * La file de moderation.
 *
 * Un seul ecran, atteint depuis les reglages, et visible seulement pour
 * qui porte un role. Il ne redessine pas un back-office : il montre le
 * contenu signale, ce qu'on lui reproche, et les deux ou trois gestes
 * possibles. Tout le reste — le droit d'agir, l'ecriture, le journal —
 * se passe dans la Cloud Function.
 *
 * Aucune ecoute, aucun rafraichissement de fond : cette file porte le
 * contenu de tout le monde, elle ne se lit que quand on l'ouvre.
 */

type Onglet = 'ouverts' | 'traites' | 'tout';

const ONGLETS: { value: Onglet; label: string }[] = [
  { value: 'ouverts', label: t.moderation.file.onglets.ouverts },
  { value: 'traites', label: t.moderation.file.onglets.traites },
  { value: 'tout', label: t.moderation.file.onglets.tout },
];

/** Les motifs, tels que l'application les enregistre. */
const MOTIFS: Record<string, string> = {
  spam: t.moderation.raisons.spam,
  harassment: t.moderation.raisons.harassment,
  shocking: t.moderation.raisons.shocking,
  misinformation: t.moderation.raisons.misinformation,
  other: t.moderation.raisons.other,
};

const NATURES: Record<Signalement['type'], string> = {
  post: t.moderation.file.natures.post,
  comment: t.moderation.file.natures.comment,
  event: t.moderation.file.natures.event,
  user: t.moderation.file.natures.user,
  conversation: t.moderation.file.natures.conversation,
  help: t.moderation.file.natures.help,
};

export function ModerationScreen() {
  const [onglet, setOnglet] = useState<Onglet>('ouverts');
  const { data, loading, error, reload } = useAsync(() => moderation.list(), []);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* Le signalement sur lequel la feuille d'actions est ouverte. */
  const [vise, setVise] = useState<Signalement | null>(null);
  const ancre = useRef<HTMLElement | null>(null);
  const { pending, request, cancel, confirm } = useConfirm();

  const liste = (data ?? []).filter((signalement) =>
    onglet === 'ouverts'
      ? !signalement.closedAt
      : onglet === 'traites'
        ? Boolean(signalement.closedAt)
        : true,
  );

  async function agir(action: ActionModeration, signalement: Signalement) {
    setVise(null);
    setBusy(true);
    setNotice(null);
    try {
      await moderation.act(action, `${NATURES[signalement.type]} · ${MOTIFS[signalement.reason]}`, signalement.id);
      reload();
      setNotice(
        action === 'masquer'
          ? t.moderation.file.masque
          : action === 'suspendre'
            ? t.moderation.file.suspendu
            : t.moderation.file.classe,
      );
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : t.sorties.commun.actionImpossible);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SubScreen
      title={t.moderation.file.titre}
      overlay={
        <>
          <Sheet open={vise !== null} anchor={ancre} onClose={() => setVise(null)}>
            {vise && (
              <>
                {vise.type !== 'user' && vise.type !== 'conversation' && vise.type !== 'help' && (
                  <SheetAction icon={<EyeSlash size={22} />} onClick={() => void agir('masquer', vise)}>
                    {t.moderation.file.masquer}
                  </SheetAction>
                )}

                {vise.vise && (
                  <SheetAction
                    icon={<Prohibit size={22} />}
                    tone="danger"
                    onClick={() => {
                      const cible = vise;
                      setVise(null);
                      request(
                        t.moderation.file.suspendreQ(cible.vise?.username ?? ''),
                        () => void agir('suspendre', cible),
                      );
                    }}
                  >
                    {t.moderation.file.suspendre}
                  </SheetAction>
                )}

                <SheetAction icon={<X size={22} />} onClick={() => void agir('classer', vise)}>
                  {t.moderation.file.classer}
                </SheetAction>
              </>
            )}
          </Sheet>

          <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />
        </>
      }
    >
      <Tabs<Onglet> value={onglet} onChange={setOnglet} options={ONGLETS} />

      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {notice && <p className="mod__notice">{notice}</p>}

      {data && liste.length === 0 && (
        <EmptyState
          title={onglet === 'ouverts' ? t.moderation.file.rienATraiter : t.moderation.file.rienIci}
          hint={
            onglet === 'ouverts'
              ? t.moderation.file.aucunEnAttente
              : t.moderation.file.traitesIci
          }
        />
      )}

      {liste.map((signalement) => (
        <article key={signalement.id} className="card mod-card">
          <header className="mod-card__head">
            <span className="mod-card__nature">{NATURES[signalement.type]}</span>
            <span className="mod-card__motif">{MOTIFS[signalement.reason] ?? signalement.reason}</span>
            <span className="mod-card__temps">{timeAgo(new Date(signalement.createdAt).toISOString())}</span>
          </header>

          {/* Le contenu tel qu'il est maintenant, pas tel qu'il etait au
              signalement : c'est celui-la qu'on juge. */}
          {signalement.extrait && (
            <p className={`mod-card__extrait${signalement.masque ? ' mod-card__extrait--masque' : ''}`}>
              {signalement.extrait}
            </p>
          )}

          {signalement.details && <p className="mod-card__details">« {signalement.details} »</p>}

          <div className="mod-card__gens">
            {signalement.vise && (
              <span className="mod-card__gen">
                <Avatar user={signalement.vise} size={26} />
                {signalement.vise.username}
                {/* Ce qu'un signalement isole ne dit pas : le premier, ou le neuvieme. */}
                {signalement.signalementsDeLAuteur > 1 && (
                  <span className="mod-card__recidive">
                    {t.moderation.file.signalements(signalement.signalementsDeLAuteur)}
                  </span>
                )}
              </span>
            )}
            <span className="mod-card__par">
              {t.moderation.file.signalePar(signalement.reporter.username)}
            </span>
          </div>

          {signalement.closedAt ? (
            <p className="mod-card__clos">
              <ShieldCheck size={15} weight="fill" />
              {signalement.status === 'masquer'
                ? t.moderation.file.etatMasque
                : signalement.status === 'suspendre'
                  ? t.moderation.file.etatSuspendu
                  : t.moderation.file.etatClasse}
            </p>
          ) : (
            <button
              className="mod-card__agir"
              disabled={busy}
              onClick={(pressed) => {
                ancre.current = pressed.currentTarget;
                setVise(signalement);
              }}
            >
              {t.moderation.file.traiter}
            </button>
          )}
        </article>
      ))}
    </SubScreen>
  );
}
